import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import CountUp from 'react-countup';

import { KLASSEN, normiereVerteilung, Verteilung } from '../klassen';
import { RegionInfoEntry } from '../regions';
import type { Level, Selection } from '../App';

interface Props {
  selection: Selection;
  /** Klassenverteilung in Hektar (das Panel zeigt Hektar, keine Prozent). */
  verteilungHa?: Verteilung;
  info?: RegionInfoEntry;
}

const EBENEN_LABEL: Record<Level, string> = {
  bundesland: 'Bundesland',
  landkreis: 'Landkreis',
  feld: 'Feld',
};

/** Kompakte Zahl im deutschen Format (Dezimaltrennzeichen Komma). */
function formatWert(wert: number): { end: number; decimals: number } {
  const decimals = Number.isInteger(wert) ? 0 : wert < 10 ? 2 : 1;
  return { end: wert, decimals };
}

/**
 * Kennzahlen/Text zur gewählten Region (§7.5).
 * Immer: die Verteilung als gestapelter Balken + Prozentwerte je Klasse.
 * Zusätzlich: generisches Rendering von `info.kennzahlen[]` + `beschreibung`.
 * Ohne Auswahl bzw. ohne Info-Eintrag entsprechende Leerzustände (kein Fehler).
 */
const InfoPanel: React.FC<Props> = ({ selection, verteilungHa, info }) => {
  if (!selection) {
    return (
      <Box className="info-panel info-panel--empty">
        <Typography className="info-panel-placeholder">
          Bitte eine Region in der Seitenleiste oder auf der Karte auswählen.
        </Typography>
      </Box>
    );
  }

  // Feld-Ebene zeigt Prozent je Klasse; Bundesland/Landkreis zeigen Hektar.
  const istFeld = selection.level === 'feld';
  const norm = normiereVerteilung(istFeld ? selection.verteilung : verteilungHa);

  // "unklassifiziert" wird nicht angezeigt (defensiv, falls in Altdaten noch
  // vorhanden — die Datengenerierung emittiert es nicht mehr).
  const kennzahlen = info?.kennzahlen?.filter((kz) => kz.label !== 'unklassifiziert');

  return (
    <Box className="info-panel" role="complementary" aria-label="Regionsdetails">
      <Box className="info-panel-head">
        <Typography variant="h2" className="info-panel-title">
          {selection.name}
          {selection.level === 'feld' && selection.sizeHa != null ? (
            <span className="info-panel-size">
              {' · '}
              {selection.sizeHa.toLocaleString('de-DE', { maximumFractionDigits: 1 })} ha
            </span>
          ) : null}
        </Typography>
        <Chip size="small" label={EBENEN_LABEL[selection.level]} className="info-panel-badge" />
      </Box>

      {/* Verteilung — immer vorhanden, falls Werte existieren */}
      {norm ? (
        <Box className="info-verteilung">
          <Box className="info-verteilung-bar">
            {norm.map((n) =>
              n.anteil > 0 ? (
                <Box
                  key={n.key}
                  className="info-verteilung-seg"
                  sx={{ width: `${n.anteil * 100}%`, backgroundColor: n.farbe }}
                  title={
                    istFeld
                      ? `${n.label}: ${Math.round(n.anteil * 100)}%`
                      : `${n.label}: ${
                          verteilungHa
                            ? (Number(verteilungHa[n.key]) || 0).toLocaleString('de-DE', {
                                maximumFractionDigits: 1,
                              })
                            : '0'
                        } ha`
                  }
                />
              ) : null
            )}
          </Box>
          <Box className="info-verteilung-legende">
            {KLASSEN.map((k) => {
              const eintrag = norm.find((n) => n.key === k.key);
              const anteil = eintrag ? eintrag.anteil : 0;
              const haWert = verteilungHa ? Number(verteilungHa[k.key]) || 0 : 0;
              return (
                <Box key={k.key} className="info-verteilung-row">
                  <Box className="info-verteilung-swatch" sx={{ backgroundColor: k.farbe }} />
                  <span className="info-verteilung-label">{k.label}</span>
                  <span className="info-verteilung-wert">
                    {istFeld ? (
                      <>
                        <CountUp end={Math.round(anteil * 100)} duration={0.8} separator="." />
                        <span className="info-einheit">&nbsp;%</span>
                      </>
                    ) : (
                      <>
                        <CountUp end={haWert} duration={0.8} decimals={1} decimal="," separator="." />
                        <span className="info-einheit">&nbsp;ha</span>
                      </>
                    )}
                  </span>
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : (
        <Typography className="info-panel-note">Keine Verteilungsdaten verfügbar.</Typography>
      )}

      {/* Zusätzliche Kennzahlen (generisch) */}
      {kennzahlen && kennzahlen.length > 0 && (
        <Box className="info-kennzahlen">
          {kennzahlen.map((kz, i) => {
            const { end, decimals } = formatWert(kz.wert);
            const dez = kz.einheit === 'ha' ? 1 : decimals;
            return (
              <Box key={`${kz.label}-${i}`} className="info-kennzahl">
                <span className="info-kennzahl-label">{kz.label}</span>
                <span className="info-kennzahl-wert">
                  <CountUp end={end} duration={0.8} decimals={dez} decimal="," separator="." />
                  {kz.einheit ? <span className="info-kennzahl-einheit">&nbsp;{kz.einheit}</span> : null}
                </span>
              </Box>
            );
          })}
        </Box>
      )}

      {info?.beschreibung ? (
        <Typography className="info-beschreibung">{info.beschreibung}</Typography>
      ) : null}
    </Box>
  );
};

export default InfoPanel;
